import {
  Button,
  Column,
  Combobox,
  FormField,
  Row,
  Spinner,
  Text,
  TextInput,
  useToast,
} from "@hightouch/htui";
import { useMemo, useState } from "react";
import { IntegrationIcon } from "src/components/integrations/integration-icon";
import { NewPage } from "src/components/layout/new-page";
import {
  AIDRole,
  useAIDPermissions,
} from "src/components/permission/use-aid-permissions";
import {
  useCreateDecisionEngineFeatureMatrixMutation,
  useGetFeatureMatrixColumnsForSegmentQuery,
  useMinimalDecisionEngineFlowsQuery,
  useMinimalParentModelsQuery,
} from "src/graphql";
import { useDecisionEngineContext } from "src/pages/decision-engines/decision-engine-context";
import { useNavigate } from "src/router";

export function CreateFeatureMatrix() {
  const { engine } = useDecisionEngineContext();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isPermitted } = useAIDPermissions({
    minRequiredRole: AIDRole.AidAdmin,
  });

  const [name, setName] = useState("");
  const [selectedParentModelId, setSelectedParentModelId] = useState<
    string | undefined
  >();

  const flowsQuery = useMinimalDecisionEngineFlowsQuery(
    { id: engine.id },
    { select: (data) => data.decision_engine_flows },
  );
  const lockedSourceId =
    flowsQuery.data?.find((flow) => flow.parent_model?.connection?.id)
      ?.parent_model?.connection?.id ?? null;

  const parentModelsQuery = useMinimalParentModelsQuery(undefined, {
    select: (data) => data.segments,
  });
  const parentModels = useMemo(
    () =>
      (parentModelsQuery.data ?? []).filter(
        (model) =>
          lockedSourceId == null || model.connection?.id === lockedSourceId,
      ),
    [parentModelsQuery.data, lockedSourceId],
  );
  const selectedParentModel = parentModels.find(
    (model) => model.id === selectedParentModelId,
  );
  const connection = selectedParentModel?.connection;

  const columnsQuery = useGetFeatureMatrixColumnsForSegmentQuery(
    {
      decisionEngineId: engine.id,
      segmentId: Number(selectedParentModelId),
    },
    {
      enabled: Boolean(selectedParentModelId),
      select: (data) => data.getFeatureMatrixColumnsForSegment,
    },
  );

  const createMutation = useCreateDecisionEngineFeatureMatrixMutation();

  const connectionName = connection
    ? connection.name || connection.definition?.name
    : null;

  const modelCount = columnsQuery.data
    ? 1 + columnsQuery.data.relationships.length
    : 0;
  const columnCount = columnsQuery.data
    ? (columnsQuery.data.parentModel?.columns.length ?? 0) +
      columnsQuery.data.relationships.reduce(
        (sum, r) => sum + r.columns.length,
        0,
      )
    : 0;

  const handleCreate = async () => {
    if (!name.trim() || !selectedParentModelId) return;
    try {
      const result = await createMutation.mutateAsync({
        object: {
          name: name.trim(),
          parent_model_id: Number(selectedParentModelId),
          decision_engine_id: engine.id,
        },
      });
      const newId = result.insert_decision_engine_feature_matrices_one?.id;
      if (newId) {
        toast({
          id: "create-feature-matrix",
          title: `Feature matrix "${name.trim()}" was created`,
          message: "Edit models to customize",
          variant: "success",
        });
        navigate(`/ai/configuration/feature-matrices/${newId}`);
      }
    } catch {
      toast({
        id: "create-feature-matrix",
        title: "Failed to create feature matrix",
        variant: "error",
      });
    }
  };

  return (
    <NewPage
      title="New feature matrix"
      crumbs={[
        {
          label: "Feature matrices",
          link: "/ai/configuration/feature-matrices",
        },
        { label: "New feature matrix" },
      ]}
    >
      <Column p={6} gap={6} maxWidth="600px" mx="auto">
        <Column gap={3}>
          <FormField
            label="Parent model"
            description="A feature matrix is built from a parent model. Agents in this decision engine must share a source, so only parent models on that source are shown."
          >
            <Combobox
              isLoading={parentModelsQuery.isLoading || flowsQuery.isLoading}
              placeholder="Select a parent model…"
              value={selectedParentModelId}
              onChange={(value) => setSelectedParentModelId(value ?? undefined)}
              optionValue={(option) => option.id}
              optionLabel={(option) => option.name}
              optionAccessories={(option) =>
                option.connection?.definition
                  ? {
                      leftAccessory: {
                        type: "image",
                        url: option.connection.definition.icon,
                      },
                    }
                  : undefined
              }
              options={parentModels}
            />
          </FormField>
          {connection && (
            <Row gap={2} align="center" color="text.tertiary">
              <IntegrationIcon
                name={connection.definition.name}
                src={connection.definition.icon}
                size={4}
              />
              <Text color="text.tertiary" size="sm">
                {connectionName}
              </Text>
            </Row>
          )}
          {columnsQuery.isLoading ? (
            <Row gap={2} align="center">
              <Spinner size="sm" />
              <Text color="text.secondary" size="sm">
                Loading columns…
              </Text>
            </Row>
          ) : columnsQuery.data ? (
            <Text color="text.secondary" size="sm">
              {modelCount} models, {columnCount} source columns found.
            </Text>
          ) : null}
        </Column>

        <Column gap={4}>
          <Column gap={1}>
            <Text fontWeight="medium">Name</Text>
            <TextInput
              placeholder="Feature matrix name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Column>

          <Row gap={3}>
            <Button
              variant="primary"
              isDisabled={
                !isPermitted ||
                !name.trim() ||
                !selectedParentModelId ||
                createMutation.isLoading
              }
              isLoading={createMutation.isLoading}
              onClick={handleCreate}
            >
              Create feature matrix
            </Button>
            <Button
              onClick={() => navigate("/ai/configuration/feature-matrices")}
            >
              Cancel
            </Button>
          </Row>
        </Column>
      </Column>
    </NewPage>
  );
}
